import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Stack,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TableSortLabel,
  TablePagination,
  Menu,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Dialog,
  DialogContent,
  DialogActions,
  Divider,
  Alert,
  CircularProgress,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import PublishOutlinedIcon from "@mui/icons-material/PublishOutlined";
import UndoOutlinedIcon from "@mui/icons-material/UndoOutlined";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import { toast, ToastContainer } from "react-toastify";
import DeleteConfirmationDialog from "../DeleteConfirmationDialog";
import RichTextEditor, { isRichTextEmpty } from "./RichTextEditor";
import { BUTTON_STYLES } from "../../styles/componentStyles";
import {
  listCourses,
  listAllSubjects,
  listAllChapters,
  listAllTopics,
  listAllSubTopics,
  apiErrorMessage,
} from "../../api/assessmentHierarchy";
import {
  QUESTION_TYPES,
  DIFFICULTIES,
  QUESTION_STATUSES,
  QUESTION_PURPOSES,
  QUESTION_USAGES,
  FINAL_OUTPUTS,
  labelOf,
  questionMediaUrl,
  listQuestions,
  createQuestion,
  updateQuestion,
  archiveQuestion,
  updateQuestionStatus,
  bulkUpdateQuestionStatus,
  bulkUploadQuestions,
  indexAssessmentChapter,
  getIndexJob,
  uploadQuestionImage,
} from "../../api/assessmentQuestions";
import { sanitizeQuestionHtml, questionPlainText, mathTextSx } from "./questionHtml";
import "katex/dist/katex.min.css";
import useDebouncedValue from "./useDebouncedValue";
import useHierarchyCascade from "./useHierarchyCascade";
import AssessmentTableSkeleton from "./AssessmentTableSkeleton";
import { cardSx, SURFACE } from "./shell/assessmentTheme";
import AssessmentEmptyState from "./shell/AssessmentEmptyState";
const ACCENT = "#17479e";

const joinNames = (names) => (names?.length ? names.join(", ") : "—");

const allColumns = [
  { key: "id", label: "ID", cellCss: { minWidth: "60px", width: "60px" }, getValue: (r) => r.id },
  { key: "question", label: "Question", cellCss: { minWidth: "320px" }, getValue: (r) => questionPlainText(r.questionText) },
  { key: "type", label: "Type", cellCss: { minWidth: "120px" }, getValue: (r) => labelOf(QUESTION_TYPES, r.questionType) },
  { key: "difficulty", label: "Difficulty", cellCss: { minWidth: "110px" }, getValue: (r) => labelOf(DIFFICULTIES, r.difficulty) },
  { key: "course", label: "Exam", cellCss: { minWidth: "150px" }, getValue: (r) => joinNames(r.courseNames) },
  { key: "subject", label: "Subject", cellCss: { minWidth: "150px" }, getValue: (r) => joinNames(r.subjectNames) },
  { key: "chapter", label: "Chapter", cellCss: { minWidth: "150px" }, getValue: (r) => joinNames(r.chapterNames) },
  { key: "topic", label: "Topic", cellCss: { minWidth: "150px" }, getValue: (r) => r.topicName || "—" },
  { key: "subTopic", label: "Sub-Topic", cellCss: { minWidth: "160px" }, getValue: (r) => r.subTopicName || "—" },
  { key: "marks", label: "Marks", cellCss: { align: "center", minWidth: "80px" }, getValue: (r) => r.marks },
  { key: "negativeMarks", label: "Negative Marks", cellCss: { align: "center", minWidth: "120px" }, getValue: (r) => r.negativeMarks },
  { key: "usage", label: "Usage", cellCss: { minWidth: "90px" }, getValue: (r) => labelOf(QUESTION_USAGES, r.questionUsage) },
  { key: "purpose", label: "Purpose", cellCss: { minWidth: "140px" }, getValue: (r) => labelOf(QUESTION_PURPOSES, r.questionPurpose) },
  { key: "finalOutput", label: "Final Output", cellCss: { minWidth: "110px" }, getValue: (r) => labelOf(FINAL_OUTPUTS, r.finalOutput) },
  { key: "category", label: "Category", cellCss: { minWidth: "120px" }, getValue: (r) => r.category || "—" },
  { key: "questionYear", label: "Year", cellCss: { minWidth: "80px" }, getValue: (r) => r.questionYear || "—" },
  { key: "examDate", label: "Exam Date", cellCss: { minWidth: "110px" }, getValue: (r) => r.examDate || "—" },
  { key: "examShift", label: "Exam Shift", cellCss: { minWidth: "110px" }, getValue: (r) => r.examShift || "—" },
  { key: "status", label: "Status", cellCss: { align: "center", minWidth: "110px" }, getValue: (r) => r.status },
];

// The picker remembers which columns were hidden (not which are shown), so
// columns added later show up by default. Until the user changes it, the less
// commonly needed columns start hidden.
const DEFAULT_HIDDEN_KEYS = [
  "course", "negativeMarks", "purpose", "finalOutput", "category",
  "questionYear", "examDate", "examShift",
];
const STORAGE_KEY = "assessmentQuestionsHiddenColumns";

const statusCfg = {
  PUBLISHED: { label: "Published", color: "#2e7d32", bg: "#e8f5e9" },
  DRAFT: { label: "Draft", color: "#555", bg: "#f0f0f0" },
  ARCHIVED: { label: "Archived", color: "#b26a00", bg: "#fff3e0" },
};

function StatusChip({ value }) {
  const cfg = statusCfg[value] ?? { label: value ?? "—", color: "#555", bg: "#f5f5f5" };
  return (
    <Chip
      label={cfg.label}
      size="small"
      variant="outlined"
      sx={{ fontWeight: 600, fontSize: "0.7rem", color: cfg.color, borderColor: cfg.color, backgroundColor: cfg.bg }}
    />
  );
}

function SectionHeader({ icon, title, subtitle }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2.5,
        py: 2,
        background: "#f7f9fd",
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <Box
        sx={{
          width: 38,
          height: 38,
          flexShrink: 0,
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: `linear-gradient(135deg, ${ACCENT} 0%, #1565c0 100%)`,
          color: "#fff",
          boxShadow: `0 4px 10px ${ACCENT}40`,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#1a1a2e" }}>{title}</Typography>
        {subtitle && <Typography sx={{ fontSize: "0.72rem", color: "text.secondary" }}>{subtitle}</Typography>}
      </Box>
    </Box>
  );
}

const OPTION_LETTERS = ["A", "B", "C", "D"];
const emptyOptions = () => ["", "", "", ""];

const FIELD_REQUIRED_MESSAGE = "Please fill out this field";

// Optional image attached to the question or to one MCQ option. Uploads the
// file immediately and reports the stored path to the parent ("" = removed).
function ImageUploadField({ label, path, onChange }) {
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const uploadedPath = await uploadQuestionImage(file);
      onChange(uploadedPath || "");
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to upload image"));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
      <Button
        component="label"
        size="small"
        variant="outlined"
        disabled={uploading}
        startIcon={uploading ? <CircularProgress size={14} /> : <ImageOutlinedIcon />}
        sx={{ textTransform: "none", fontSize: "0.72rem" }}
      >
        {path ? `Change ${label}` : `Add ${label}`}
        <input type="file" hidden accept="image/*" onChange={handleFile} />
      </Button>
      {path && (
        <>
          <Box
            component="img"
            src={questionMediaUrl(path)}
            alt={label}
            sx={{ height: 36, maxWidth: 80, objectFit: "contain", borderRadius: 1, border: "1px solid #e0e0e0" }}
          />
          <Tooltip title={`Remove ${label}`} arrow>
            <IconButton size="small" onClick={() => onChange("")}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      )}
    </Stack>
  );
}

function QuestionFormDialog({ open, item, hierarchy, onClose, onSave }) {
  const cascade = useHierarchyCascade(hierarchy);
  const [question, setQuestion] = useState("");
  const [questionTouched, setQuestionTouched] = useState(false);
  const [questionImage, setQuestionImage] = useState("");
  const [type, setType] = useState("MCQ");
  const [options, setOptions] = useState(emptyOptions());
  const [optionImages, setOptionImages] = useState(emptyOptions());
  const [correctOptions, setCorrectOptions] = useState([]);
  const [correctBoolean, setCorrectBoolean] = useState("");
  const [correctAnswerText, setCorrectAnswerText] = useState("");
  const [explanation, setExplanation] = useState("");
  const [difficulty, setDifficulty] = useState("MODERATE");
  const [marks, setMarks] = useState("4");
  const [negativeMarks, setNegativeMarks] = useState("1");
  const [category, setCategory] = useState("");
  const [usage, setUsage] = useState("NEW");
  const [purpose, setPurpose] = useState("");
  const [finalOutput, setFinalOutput] = useState("APP_PURPOSE");
  const [questionYear, setQuestionYear] = useState("");
  const [examDate, setExamDate] = useState("");
  const [examShift, setExamShift] = useState("");
  const [submittingAction, setSubmittingAction] = useState(null);

  useEffect(() => {
    if (!open) {
      setSubmittingAction(null);
      return;
    }
    setQuestionTouched(false);
    setQuestion(item?.questionText || "");
    setQuestionImage(item?.questionImagePath || "");
    setType(item?.questionType || "MCQ");
    setOptions(
      item?.questionType === "MCQ"
        ? [item.optionAText, item.optionBText, item.optionCText, item.optionDText].map((o) => o || "")
        : emptyOptions(),
    );
    setOptionImages(
      item
        ? [item.optionAImagePath, item.optionBImagePath, item.optionCImagePath, item.optionDImagePath].map((o) => o || "")
        : emptyOptions(),
    );
    setCorrectOptions(
      item?.correctOptions?.length ? [...item.correctOptions] : item?.correctOption ? [item.correctOption] : [],
    );
    setCorrectBoolean(
      item?.correctBooleanAnswer === true ? "true" : item?.correctBooleanAnswer === false ? "false" : "",
    );
    setCorrectAnswerText(item?.correctAnswerText || "");
    setExplanation(item?.explanationText || "");
    setDifficulty(item?.difficulty || "MODERATE");
    setMarks(item?.marks != null ? String(item.marks) : "4");
    setNegativeMarks(item?.negativeMarks != null ? String(item.negativeMarks) : "1");
    setCategory(item?.category || "");
    setUsage(item?.questionUsage || "NEW");
    setPurpose(item?.questionPurpose || "");
    setFinalOutput(item?.finalOutput || "APP_PURPOSE");
    setQuestionYear(item?.questionYear || "");
    setExamDate(item?.examDate || "");
    setExamShift(item?.examShift || "");

    if (item) {
      // A question stores one topic; the chain above it is many-to-many, so
      // pick the first course/subject/chapter path that actually connects.
      const courseId = item.courseIds?.[0] ?? "";
      const subject = hierarchy.subjects.find(
        (s) => item.subjectIds?.includes(s.id) && s.courseIds?.includes(courseId),
      );
      const chapter = hierarchy.chapters.find(
        (c) => item.chapterIds?.includes(c.id) && c.subjectIds?.includes(subject?.id),
      );
      cascade.setCourseId(courseId);
      cascade.setSubjectId(subject?.id ?? "");
      cascade.setChapterId(chapter?.id ?? "");
      cascade.setTopicId(item.topicId ?? "");
      cascade.setSubTopicId(item.subTopicId ?? "");
    } else {
      cascade.setCourseId("");
      cascade.setSubjectId("");
      cascade.setChapterId("");
      cascade.setTopicId("");
      cascade.setSubTopicId("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item]);

  const updateOption = (idx, value) => {
    setOptions((prev) => prev.map((o, i) => (i === idx ? value : o)));
  };
  const updateOptionImage = (idx, value) => {
    setOptionImages((prev) => prev.map((o, i) => (i === idx ? value : o)));
  };
  const toggleCorrectOption = (letter) => {
    setCorrectOptions((prev) => (prev.includes(letter) ? prev.filter((l) => l !== letter) : [...prev, letter]));
  };

  const isMcq = type === "MCQ";
  const isPyq = usage === "PYQ";
  const marksNumber = Number(marks);
  const negativeNumber = Number(negativeMarks);
  const marksInvalid = marks === "" || !(marksNumber > 0);
  const negativeInvalid = negativeMarks !== "" && negativeNumber < 0;
  const questionEmpty = isRichTextEmpty(question);

  const typeComplete = (() => {
    switch (type) {
      case "MCQ":
        return (
          options.every((o) => !isRichTextEmpty(o)) &&
          correctOptions.length > 0 &&
          !isRichTextEmpty(explanation)
        );
      case "TRUE_FALSE":
        return correctBoolean !== "";
      case "FILL_IN_THE_BLANK":
        return correctAnswerText.trim().length > 0;
      default:
        return true;
    }
  })();

  const isValid =
    !questionEmpty &&
    cascade.courseId !== "" &&
    cascade.subjectId !== "" &&
    cascade.chapterId !== "" &&
    cascade.topicId !== "" &&
    !marksInvalid &&
    !negativeInvalid &&
    typeComplete &&
    (!isPyq || (questionYear.trim() && examDate.trim()));

  const buildPayload = () => {
    // On edit, an emptied optional field is sent as "" so it clears the stored
    // value; on create it is simply omitted.
    const optional = (value) => {
      const trimmed = typeof value === "string" ? value.trim() : value;
      return trimmed || (item ? "" : undefined);
    };
    const payload = {
      id: item?.id,
      courseId: cascade.courseId,
      subjectId: cascade.subjectId,
      chapterId: cascade.chapterId,
      topicId: cascade.topicId,
      subTopicId: cascade.subTopicId === "" ? undefined : cascade.subTopicId,
      questionText: question,
      questionImagePath: optional(questionImage),
      questionType: type,
      marks: marksNumber,
      negativeMarks: negativeMarks === "" ? 0 : negativeNumber,
      difficulty,
      category: optional(category),
      questionUsage: usage,
      questionPurpose: purpose || undefined,
      finalOutput,
      questionYear: isPyq ? questionYear.trim() : optional(""),
      examDate: isPyq ? examDate.trim() : optional(""),
      examShift: isPyq ? optional(examShift) : optional(""),
      explanationText: isRichTextEmpty(explanation) ? optional("") : explanation,
    };
    if (isMcq) {
      payload.optionAText = options[0];
      payload.optionBText = options[1];
      payload.optionCText = options[2];
      payload.optionDText = options[3];
      payload.optionAImagePath = optional(optionImages[0]);
      payload.optionBImagePath = optional(optionImages[1]);
      payload.optionCImagePath = optional(optionImages[2]);
      payload.optionDImagePath = optional(optionImages[3]);
      payload.correctOptions = OPTION_LETTERS.filter((l) => correctOptions.includes(l));
    } else if (type === "TRUE_FALSE") {
      payload.correctBooleanAnswer = correctBoolean === "true";
    } else {
      payload.correctAnswerText = correctAnswerText.trim() || optional("");
    }
    return payload;
  };

  const handleSubmit = async (publish) => {
    if (!isValid || submittingAction) return;
    setSubmittingAction(publish ? "publish" : "save");
    try {
      await onSave(buildPayload(), publish);
    } finally {
      setSubmittingAction(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" PaperProps={{ sx: { borderRadius: 3, backgroundColor: SURFACE.page, overflow: "hidden" } }}>
      <Box
        sx={{
          position: "relative",
          p: 3,
          background: "linear-gradient(120deg, #0f3375 0%, #17479e 55%, #3b6fd4 100%)",
          color: "#fff",
        }}
      >
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ position: "absolute", top: 10, right: 10, color: "rgba(255,255,255,0.85)" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 44,
              height: 44,
              flexShrink: 0,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255,255,255,0.18)",
            }}
          >
            <QuizOutlinedIcon />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: "1.15rem" }}>
              {item ? "Edit Question" : "Add New Question"}
            </Typography>
            <Typography sx={{ fontSize: "0.78rem", opacity: 0.85 }}>
              {item ? "Update content, mapping and settings" : "Author content, map it and set difficulty"}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.75 }}>
          <Chip label={labelOf(QUESTION_TYPES, type)} size="small" sx={{ bgcolor: "rgba(255,255,255,0.18)", color: "#fff", fontWeight: 600 }} />
          <Chip label={labelOf(DIFFICULTIES, difficulty)} size="small" sx={{ bgcolor: "rgba(255,255,255,0.18)", color: "#fff", fontWeight: 600 }} />
          <Chip label={labelOf(QUESTION_USAGES, usage)} size="small" sx={{ bgcolor: "rgba(255,255,255,0.28)", color: "#fff", fontWeight: 700 }} />
        </Stack>
      </Box>
      <DialogContent sx={{ pt: 3, backgroundColor: SURFACE.page }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.6fr 1fr" }, gap: 3, alignItems: "start" }}>
          {/* Question Content */}
          <Card sx={{ ...cardSx, overflow: "hidden" }}>
            <SectionHeader
              icon={<QuizOutlinedIcon sx={{ fontSize: 20 }} />}
              title="Question Content"
              subtitle="Question, answer options and explanation"
            />
            <CardContent sx={{ p: 3 }}>
              <FormControl size="small" required sx={{ minWidth: 200, mb: 2.5 }}>
                <InputLabel>Question Type</InputLabel>
                <Select label="Question Type *" value={type} onChange={(e) => setType(e.target.value)}>
                  {QUESTION_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Typography sx={{ fontWeight: 600, fontSize: "0.8rem", mb: 0.75 }}>Question Text *</Typography>
              <Box
                sx={{ mb: questionTouched && questionEmpty ? 0.5 : 1 }}
                onBlur={() => setQuestionTouched(true)}
              >
                <RichTextEditor
                  value={question}
                  onChange={setQuestion}
                  placeholder="Enter the question... use the fx button for math formulas"
                />
              </Box>
              {questionTouched && questionEmpty && (
                <Typography sx={{ color: "error.main", fontSize: "0.75rem", mb: 1 }}>{FIELD_REQUIRED_MESSAGE}</Typography>
              )}
              <Box sx={{ mb: 3 }}>
                <ImageUploadField label="Question Image" path={questionImage} onChange={setQuestionImage} />
              </Box>

              {isMcq && (
                <>
                  <Typography sx={{ fontWeight: 600, fontSize: "0.8rem", mb: 1 }}>
                    Options * (click a row to mark it correct; select all that apply)
                  </Typography>
                  <Stack spacing={1.5} sx={{ mb: 3 }}>
                    {options.map((opt, idx) => {
                      const letter = OPTION_LETTERS[idx];
                      const selected = correctOptions.includes(letter);
                      return (
                        <Box
                          key={letter}
                          sx={{
                            border: "1px solid",
                            borderColor: selected ? "#2e7d32" : "#e0e0e0",
                            borderRadius: 2,
                            borderLeftWidth: 4,
                            borderLeftColor: selected ? "#2e7d32" : "#d5dbe6",
                            backgroundColor: selected ? "#eaf7ec" : "#fff",
                            overflow: "hidden",
                            transition: "all 0.15s ease-in-out",
                          }}
                        >
                          <Box
                            onClick={() => toggleCorrectOption(letter)}
                            sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 1.25, py: 1, cursor: "pointer" }}
                          >
                            <Box
                              sx={{
                                width: 26,
                                height: 26,
                                borderRadius: "50%",
                                border: "2px solid",
                                borderColor: selected ? "#2e7d32" : "#bbb",
                                backgroundColor: selected ? "#2e7d32" : "transparent",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontWeight: 700,
                                fontSize: "0.75rem",
                                color: selected ? "#fff" : "#777",
                                flexShrink: 0,
                              }}
                            >
                              {letter}
                            </Box>
                            <Typography sx={{ fontWeight: 600, fontSize: "0.75rem", color: selected ? "#2e7d32" : "#777", flex: 1 }}>
                              Option {letter}
                            </Typography>
                            {selected && <CheckCircleIcon sx={{ color: "#2e7d32" }} fontSize="small" />}
                          </Box>
                          <RichTextEditor
                            compact
                            minHeight={44}
                            value={opt}
                            onChange={(v) => updateOption(idx, v)}
                            placeholder={`Enter option ${letter}...`}
                          />
                          <Box sx={{ px: 1.25, pb: 1 }}>
                            <ImageUploadField
                              label={`Option ${letter} Image`}
                              path={optionImages[idx]}
                              onChange={(v) => updateOptionImage(idx, v)}
                            />
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                </>
              )}

              {type === "TRUE_FALSE" && (
                <FormControl fullWidth size="small" required sx={{ mb: 3 }}>
                  <InputLabel>Correct Answer</InputLabel>
                  <Select label="Correct Answer *" value={correctBoolean} onChange={(e) => setCorrectBoolean(e.target.value)}>
                    <MenuItem value="true">True</MenuItem>
                    <MenuItem value="false">False</MenuItem>
                  </Select>
                </FormControl>
              )}

              {type === "FILL_IN_THE_BLANK" && (
                <TextField
                  label="Correct Answer"
                  fullWidth
                  size="small"
                  required
                  value={correctAnswerText}
                  onChange={(e) => setCorrectAnswerText(e.target.value)}
                  error={correctAnswerText !== "" && !correctAnswerText.trim()}
                  helperText={correctAnswerText !== "" && !correctAnswerText.trim() ? FIELD_REQUIRED_MESSAGE : undefined}
                  sx={{ mb: 3 }}
                />
              )}

              {type === "DESCRIPTIVE" && (
                <TextField
                  label="Model Answer (optional)"
                  fullWidth
                  size="small"
                  multiline
                  minRows={3}
                  value={correctAnswerText}
                  onChange={(e) => setCorrectAnswerText(e.target.value)}
                  sx={{ mb: 3 }}
                />
              )}

              <Typography sx={{ fontWeight: 600, fontSize: "0.8rem", mb: 0.75 }}>
                {isMcq ? "Explanation / Solution *" : "Explanation / Solution"}
              </Typography>
              <RichTextEditor
                minHeight={90}
                value={explanation}
                onChange={setExplanation}
                placeholder="Explain the correct answer... use the fx button for math formulas"
              />
            </CardContent>
          </Card>

          {/* Right column: Mapping + Settings */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Card sx={{ ...cardSx, overflow: "hidden" }}>
              <SectionHeader
                icon={<AccountTreeOutlinedIcon sx={{ fontSize: 20 }} />}
                title="Mapping"
                subtitle="Exam → Subject → Chapter → Topic → Sub-Topic"
              />
              <CardContent sx={{ p: 3 }}>
                <Stack spacing={2}>
                  <FormControl fullWidth size="small" required>
                    <InputLabel>Exam</InputLabel>
                    <Select label="Exam *" value={cascade.courseId} onChange={(e) => cascade.setCourseId(e.target.value)}>
                      {hierarchy.courses.map((c) => (
                        <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth size="small" required>
                    <InputLabel>Subject</InputLabel>
                    <Select label="Subject *" value={cascade.subjectId} onChange={(e) => cascade.setSubjectId(e.target.value)}>
                      {cascade.subjectOptions.map((s) => (
                        <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth size="small" required>
                    <InputLabel>Chapter</InputLabel>
                    <Select label="Chapter *" value={cascade.chapterId} onChange={(e) => cascade.setChapterId(e.target.value)}>
                      {cascade.chapterOptions.map((c) => (
                        <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth size="small" required>
                    <InputLabel>Topic</InputLabel>
                    <Select label="Topic *" value={cascade.topicId} onChange={(e) => cascade.setTopicId(e.target.value)}>
                      {cascade.topicOptions.map((t) => (
                        <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth size="small">
                    <InputLabel>Sub-Topic (optional)</InputLabel>
                    <Select label="Sub-Topic (optional)" value={cascade.subTopicId} onChange={(e) => cascade.setSubTopicId(e.target.value)}>
                      <MenuItem value="">None</MenuItem>
                      {cascade.subTopicOptions.map((st) => (
                        <MenuItem key={st.id} value={st.id}>{st.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              </CardContent>
            </Card>

            <Card sx={{ ...cardSx, overflow: "hidden" }}>
              <SectionHeader
                icon={<TuneOutlinedIcon sx={{ fontSize: 20 }} />}
                title="Settings"
                subtitle="Difficulty, usage, marks and output"
              />
              <CardContent sx={{ p: 3 }}>
                <Stack spacing={2}>
                  <FormControl fullWidth size="small" required>
                    <InputLabel>Difficulty</InputLabel>
                    <Select label="Difficulty *" value={difficulty} onChange={(e) => setDifficulty(e.target.value)}>
                      {DIFFICULTIES.map((d) => (
                        <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth size="small">
                    <InputLabel>Usage</InputLabel>
                    <Select label="Usage" value={usage} onChange={(e) => setUsage(e.target.value)}>
                      {QUESTION_USAGES.map((u) => (
                        <MenuItem key={u.value} value={u.value}>{u.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {isPyq && (
                    <>
                      <TextField
                        label="Question Year"
                        size="small"
                        fullWidth
                        required
                        value={questionYear}
                        onChange={(e) => setQuestionYear(e.target.value)}
                        error={questionYear !== "" && !questionYear.trim()}
                        helperText={questionYear !== "" && !questionYear.trim() ? FIELD_REQUIRED_MESSAGE : undefined}
                      />
                      <TextField
                        label="Exam Date"
                        size="small"
                        fullWidth
                        required
                        value={examDate}
                        onChange={(e) => setExamDate(e.target.value)}
                        error={examDate !== "" && !examDate.trim()}
                        helperText={examDate !== "" && !examDate.trim() ? FIELD_REQUIRED_MESSAGE : undefined}
                      />
                      <TextField
                        label="Exam Shift (optional)"
                        size="small"
                        fullWidth
                        value={examShift}
                        onChange={(e) => setExamShift(e.target.value)}
                      />
                    </>
                  )}
                  <FormControl fullWidth size="small">
                    <InputLabel>Purpose (optional)</InputLabel>
                    <Select label="Purpose (optional)" value={purpose} onChange={(e) => setPurpose(e.target.value)}>
                      <MenuItem value="">None</MenuItem>
                      {QUESTION_PURPOSES.map((p) => (
                        <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth size="small">
                    <InputLabel>Final Output</InputLabel>
                    <Select label="Final Output" value={finalOutput} onChange={(e) => setFinalOutput(e.target.value)}>
                      {FINAL_OUTPUTS.map((f) => (
                        <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label="Marks"
                    type="number"
                    size="small"
                    fullWidth
                    required
                    value={marks}
                    onChange={(e) => setMarks(e.target.value)}
                    error={marksInvalid}
                    helperText={marksInvalid ? "Must be greater than 0" : undefined}
                  />
                  <TextField
                    label="Negative Marks"
                    type="number"
                    size="small"
                    fullWidth
                    value={negativeMarks}
                    onChange={(e) => setNegativeMarks(e.target.value)}
                    error={negativeInvalid}
                    helperText={negativeInvalid ? "Must be 0 or more (enter the marks deducted)" : undefined}
                  />
                  <TextField
                    label="Category (optional)"
                    size="small"
                    fullWidth
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                  />
                </Stack>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2, backgroundColor: SURFACE.page }}>
        <Box sx={{ display: "flex", width: "100%", gap: 1.5 }}>
          <LoadingButton
            variant="contained"
            disableElevation
            loading={submittingAction === "save"}
            onClick={() => handleSubmit(false)}
            disabled={!isValid || Boolean(submittingAction)}
            sx={{
              flex: "0 0 auto",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              color: "#555",
              backgroundColor: "#eceff1",
              "&:hover": { backgroundColor: "#e0e3e6" },
            }}
          >
            {item ? "Save Changes" : "Save as Draft"}
          </LoadingButton>
          <LoadingButton
            variant="contained"
            disableElevation
            loading={submittingAction === "publish"}
            onClick={() => handleSubmit(true)}
            disabled={!isValid || Boolean(submittingAction)}
            sx={{
              flex: 1,
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${ACCENT} 0%, #1565c0 100%)`,
              "&:hover": { background: `linear-gradient(135deg, #123a80 0%, #0d47a1 100%)` },
              "&.Mui-disabled": { background: "#b0bec5" },
            }}
          >
            {item ? "Save & Publish" : "Publish Question"}
          </LoadingButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

function BulkUploadDialog({ open, onClose, onUploaded }) {
  const [contentType, setContentType] = useState(null); // "SYNOPSIS" | "QUESTION_BANK"
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null); // per-file outcomes after an upload run

  useEffect(() => {
    if (!open) {
      setSubmitting(false);
      setContentType(null);
      setFiles([]);
      setResults(null);
    }
  }, [open]);

  const isSynopsis = contentType === "SYNOPSIS";

  const handleFilesPicked = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    if (!picked.length) return;
    const keyOf = (f) => `${f.name}:${f.size}:${f.lastModified}`;
    const seen = new Set(files.map(keyOf));
    const fresh = [];
    picked.forEach((f) => {
      const k = keyOf(f);
      if (!seen.has(k)) {
        seen.add(k);
        fresh.push(f);
      }
    });
    const skipped = picked.length - fresh.length;
    if (skipped) toast.info(`${skipped} duplicate file${skipped > 1 ? "s" : ""} skipped`);
    setFiles((prev) => [...prev, ...fresh]);
    setResults(null);
  };

  // The backend rejects a file whose detected type differs from the chosen one.
  const handleUpload = async () => {
    if (!files.length || !contentType || submitting) return;
    setSubmitting(true);
    const outcomes = [];
    let anyCreated = false;
    for (const file of files) {
      try {
        const res = (await bulkUploadQuestions(file, contentType)) || { createdCount: 0, errors: [] };
        if (res.synopsisChapterId != null || res.createdCount > 0) anyCreated = true;
        // A saved synopsis is indexed right away; an indexing failure must not mask the successful upload.
        let indexError = null;
        let indexJobId = null;
        if (res.synopsisChapterId != null) {
          try {
            indexJobId = (await indexAssessmentChapter(res.synopsisChapterId))?.jobId ?? null;
          } catch (err) {
            indexError = apiErrorMessage(err, "Failed to index synopsis");
          }
        }
        outcomes.push({ name: file.name, res, indexError, indexJobId, indexStatus: indexJobId ? "PENDING" : null });
      } catch (err) {
        outcomes.push({ name: file.name, failure: apiErrorMessage(err, "Failed to upload file") });
      }
    }
    setResults(outcomes);
    setFiles([]);
    setSubmitting(false);
    if (anyCreated) onUploaded();
    outcomes.forEach((o, idx) => o.indexJobId && watchIndexJob(o, idx));
  };

  // Indexing runs async on the backend. Poll its job so the dialog shows the outcome, and toast it
  // too since the dialog may already be closed by then (the loop keeps running after close).
  const watchIndexJob = async (outcome, idx) => {
    const patch = (changes) =>
      setResults((prev) => (prev ? prev.map((r, i) => (i === idx ? { ...r, ...changes } : r)) : prev));
    for (let attempt = 0; attempt < 150; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, 2000));
      try {
        const job = await getIndexJob(outcome.indexJobId);
        if (job?.status === "SUCCEEDED") {
          patch({ indexStatus: "SUCCEEDED" });
          toast.success(`${outcome.name}: synopsis indexed`);
          return;
        }
        if (job?.status === "FAILED") {
          const message = job.errorMessage || "Indexing failed";
          patch({ indexStatus: "FAILED", indexError: message });
          toast.error(`${outcome.name}: ${message}`);
          return;
        }
        patch({ indexStatus: job?.status || "PENDING" });
      } catch (err) {
        patch({ indexStatus: "FAILED", indexError: apiErrorMessage(err, "Could not check indexing status") });
        return;
      }
    }
    patch({ indexStatus: "FAILED", indexError: "Timed out waiting for indexing to finish" });
  };

  const typeOptions = [
    { key: "SYNOPSIS", label: "Synopsis", hint: "Chapter synopsis documents" },
    { key: "QUESTION_BANK", label: "Questions", hint: "Question bank documents" },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: 3, backgroundColor: SURFACE.page, overflow: "hidden" } }}
    >
      <Box
        sx={{
          position: "relative",
          p: 2.5,
          background: `linear-gradient(135deg, ${ACCENT} 0%, #1565c0 100%)`,
          color: "#fff",
        }}
      >
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ position: "absolute", top: 10, right: 10, color: "rgba(255,255,255,0.85)" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255,255,255,0.18)",
            }}
          >
            <UploadFileIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: "1.05rem" }}>Upload Content</Typography>
            <Typography sx={{ fontSize: "0.78rem", opacity: 0.85 }}>Import synopsis or questions from Word files</Typography>
          </Box>
        </Stack>
      </Box>
      <DialogContent sx={{ pt: 3, backgroundColor: SURFACE.page }}>
        <Card sx={{ ...cardSx, overflow: "hidden" }}>
          <CardContent sx={{ p: 3 }}>
            <FormControl fullWidth size="small" sx={{ mb: 2.5 }} disabled={submitting}>
              <InputLabel id="upload-content-type-label">Upload type</InputLabel>
              <Select
                labelId="upload-content-type-label"
                label="Upload type"
                value={contentType || ""}
                onChange={(e) => {
                  setContentType(e.target.value);
                  setFiles([]);
                  setResults(null);
                }}
              >
                {typeOptions.map((opt) => (
                  <MenuItem key={opt.key} value={opt.key}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {contentType && (
              <>
                <Box
                  component="label"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.75,
                    p: 2,
                    borderRadius: 2,
                    border: "1.5px dashed",
                    borderColor: files.length ? "#2e7d32" : `${ACCENT}66`,
                    backgroundColor: files.length ? "#eaf7ec" : `${ACCENT}08`,
                    cursor: submitting ? "default" : "pointer",
                    transition: "all 0.15s ease-in-out",
                    "&:hover": {
                      borderColor: files.length ? "#2e7d32" : ACCENT,
                      backgroundColor: files.length ? "#e3f3e5" : `${ACCENT}12`,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      flexShrink: 0,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: files.length ? "#2e7d32" : ACCENT,
                      color: "#fff",
                    }}
                  >
                    {files.length ? <CheckCircleIcon fontSize="small" /> : <UploadFileIcon fontSize="small" />}
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      noWrap
                      sx={{ fontWeight: 600, fontSize: "0.85rem", color: files.length ? "#2e7d32" : "#1a1a2e" }}
                    >
                      {files.length
                        ? `${files.length} file${files.length > 1 ? "s" : ""} selected`
                        : `Click to choose ${isSynopsis ? "synopsis" : "question"} files`}
                    </Typography>
                    <Typography sx={{ fontSize: "0.72rem", color: "text.secondary" }}>
                      {files.length ? "Click to add more files" : ".docx format only — you can select multiple files"}
                    </Typography>
                  </Box>
                  <input
                    type="file"
                    hidden
                    multiple
                    accept=".docx"
                    disabled={submitting}
                    onChange={handleFilesPicked}
                  />
                </Box>

                {files.length > 0 && (
                  <Stack spacing={0.5} sx={{ mt: 1.5, maxHeight: 160, overflowY: "auto" }}>
                    {files.map((f, i) => (
                      <Stack key={`${f.name}:${f.size}`} direction="row" alignItems="center" spacing={1}>
                        <Typography noWrap sx={{ flex: 1, fontSize: "0.78rem" }}>
                          {f.name}
                        </Typography>
                        <IconButton
                          size="small"
                          disabled={submitting}
                          onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}
                        >
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </>
            )}

            {results && (
              <Stack spacing={1.5} sx={{ mt: 2, maxHeight: 260, overflowY: "auto" }}>
                {results.map((r, idx) => {
                  const errs = r.res?.errors || [];
                  const savedSynopsis = r.res?.synopsisChapterId != null;
                  const indexing = r.indexStatus === "PENDING" || r.indexStatus === "RUNNING";
                  const ok =
                    !r.failure && !r.indexError && !indexing && errs.length === 0 && (savedSynopsis || r.res.createdCount > 0);
                  const severity = r.failure
                    ? "error"
                    : ok
                      ? "success"
                      : errs.length || r.indexError
                        ? "warning"
                        : "info";
                  let summary;
                  if (r.failure) summary = r.failure;
                  else if (savedSynopsis)
                    summary = r.indexError
                      ? `Synopsis saved, but indexing failed (${r.indexError})`
                      : indexing
                        ? "Synopsis saved, indexing in progress…"
                        : "Synopsis saved and indexed";
                  else summary = `${r.res.createdCount} question(s) created`;
                  return (
                    <Alert key={idx} severity={severity} sx={{ alignItems: "flex-start" }}>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.78rem" }}>{r.name}</Typography>
                      <Typography sx={{ fontSize: "0.78rem" }}>
                        {summary}
                        {!r.failure && !savedSynopsis && errs.length ? `, ${errs.length} row(s) failed` : ""}.
                      </Typography>
                      {errs.map((e, i) => (
                        <Typography key={i} sx={{ fontSize: "0.75rem", color: "error.main" }}>
                          {e.question > 0 ? `Question ${e.question}: ` : ""}
                          {e.errorMessage}
                        </Typography>
                      ))}
                    </Alert>
                  );
                })}
              </Stack>
            )}
          </CardContent>
        </Card>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, backgroundColor: SURFACE.page }}>
        <Box sx={{ display: "flex", width: "100%", gap: 1.5 }}>
          <Button
            onClick={onClose}
            disabled={submitting}
            sx={{
              flex: "0 0 auto",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              color: "#555",
              backgroundColor: "#eceff1",
              "&:hover": { backgroundColor: "#e0e3e6" },
            }}
          >
            {results ? "Close" : "Cancel"}
          </Button>
          <LoadingButton
            variant="contained"
            disableElevation
            loading={submitting}
            onClick={handleUpload}
            disabled={!files.length || !contentType}
            sx={{
              flex: 1,
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${ACCENT} 0%, #1565c0 100%)`,
              "&:hover": { background: `linear-gradient(135deg, #123a80 0%, #0d47a1 100%)` },
              "&.Mui-disabled": { background: "#b0bec5" },
            }}
          >
            Upload
          </LoadingButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

const compareValues = (a, b) => {
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true });
};

// Alphabetical copy of filter options; hierarchy items carry `name`, enum items carry `label`.
const sortByLabel = (items) =>
  [...(items || [])].sort((a, b) => compareValues(a.name ?? a.label, b.name ?? b.label));

function AssessmentQuestions() {
  const [rows, setRows] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [courses, setCourses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [topics, setTopics] = useState([]);
  const [subTopics, setSubTopics] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search);
  const filterCascade = useHierarchyCascade({ courses, subjects, chapters, topics, subTopics });
  const [difficultyFilter, setDifficultyFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [usageFilter, setUsageFilter] = useState("");
  const [finalOutputFilter, setFinalOutputFilter] = useState("");
  const [purposeFilter, setPurposeFilter] = useState("");

  // The backend has no sort parameter, so sorting only reorders the current page.
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("asc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null); // { ids: number[] }
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const [visibleColumns, setVisibleColumns] = useState(() => {
    let hidden = DEFAULT_HIDDEN_KEYS;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) hidden = parsed;
      }
    } catch {}
    const visible = allColumns.map((c) => c.key).filter((k) => !hidden.includes(k));
    return new Set(visible.length > 0 ? visible : allColumns.map((c) => c.key));
  });
  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState(null);
  const openColumnsMenu = Boolean(columnsMenuAnchor);

  // Guards against an older, slower list response overwriting a newer one.
  const requestSeq = useRef(0);

  const loadQuestions = () => {
    const seq = ++requestSeq.current;
    setLoading(true);
    listQuestions({
      page,
      size: rowsPerPage,
      search: debouncedSearch.trim(),
      courseId: filterCascade.courseId,
      subjectId: filterCascade.subjectId,
      chapterId: filterCascade.chapterId,
      topicId: filterCascade.topicId,
      subTopicId: filterCascade.subTopicId,
      difficulty: difficultyFilter,
      questionType: typeFilter,
      status: statusFilter,
      questionUsage: usageFilter,
      finalOutput: finalOutputFilter,
      questionPurpose: purposeFilter,
    })
      .then((data) => {
        if (seq !== requestSeq.current) return;
        setRows(data.content || []);
        setTotalElements(data.totalElements ?? 0);
      })
      .catch((err) => {
        if (seq !== requestSeq.current) return;
        setRows([]);
        setTotalElements(0);
        toast.error(apiErrorMessage(err, "Failed to load questions"));
      })
      .finally(() => {
        if (seq === requestSeq.current) setLoading(false);
      });
  };

  useEffect(() => {
    Promise.all([listCourses({ includeInactive: true }), listAllSubjects(), listAllChapters(), listAllTopics(), listAllSubTopics()])
      .then(([courseRows, subjectRows, chapterRows, topicRows, subTopicRows]) => {
        setCourses(courseRows);
        setSubjects(subjectRows);
        setChapters(chapterRows);
        setTopics(topicRows);
        setSubTopics(subTopicRows);
      })
      .catch((err) => toast.error(apiErrorMessage(err, "Failed to load exam hierarchy")));
  }, []);

  useEffect(() => {
    setPage(0);
  }, [
    debouncedSearch, filterCascade.courseId, filterCascade.subjectId, filterCascade.chapterId,
    filterCascade.topicId, filterCascade.subTopicId, difficultyFilter, typeFilter, statusFilter, usageFilter, finalOutputFilter,
    purposeFilter,
  ]);

  useEffect(() => {
    loadQuestions();
    setSelectedIds(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page, rowsPerPage, debouncedSearch, filterCascade.courseId, filterCascade.subjectId, filterCascade.chapterId,
    filterCascade.topicId, filterCascade.subTopicId, difficultyFilter, typeFilter, statusFilter, usageFilter, finalOutputFilter,
    purposeFilter,
  ]);

  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(allColumns.map((c) => c.key).filter((k) => !visibleColumns.has(k))),
      );
    } catch {}
  }, [visibleColumns]);

  const handleOpenColumnsMenu = (e) => setColumnsMenuAnchor(e.currentTarget);
  const handleCloseColumnsMenu = () => setColumnsMenuAnchor(null);
  const toggleColumn = (key) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const visibleColumnDefs = allColumns.filter((c) => visibleColumns.has(c.key));

  const sortedData = useMemo(() => {
    const col = allColumns.find((c) => c.key === orderBy);
    if (!col) return rows;
    return [...rows].sort((a, b) => {
      const cmp = compareValues(col.getValue(a), col.getValue(b));
      return order === "asc" ? cmp : -cmp;
    });
  }, [rows, orderBy, order]);

  const handleSort = (columnKey) => {
    const isAsc = orderBy === columnKey && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(columnKey);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const allOnPageSelected = sortedData.length > 0 && sortedData.every((r) => selectedIds.has(r.id));
  const someOnPageSelected = sortedData.some((r) => selectedIds.has(r.id));
  const toggleSelectAll = () => {
    setSelectedIds(allOnPageSelected ? new Set() : new Set(sortedData.map((r) => r.id)));
  };
  const toggleSelectRow = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openAdd = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (item) => {
    setEditing(item);
    setFormOpen(true);
  };

  const handleSave = async (values, publish) => {
    const { id, ...payload } = values;
    let saved;
    try {
      saved = id ? await updateQuestion(id, payload) : await createQuestion(payload);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to save question"));
      return;
    }
    const questionId = id || saved?.id;
    if (publish && questionId) {
      try {
        await updateQuestionStatus(questionId, "PUBLISHED");
        toast.success(id ? "Question updated and published!" : "Question created and published!");
      } catch (err) {
        toast.warning(`Question saved, but publishing failed: ${apiErrorMessage(err, "unknown error")}`);
      }
    } else {
      toast.success(id ? "Question updated successfully!" : "Question created as draft!");
    }
    setFormOpen(false);
    loadQuestions();
  };

  const changeStatus = async (id, status) => {
    try {
      await updateQuestionStatus(id, status);
      toast.success(`Question ${labelOf(QUESTION_STATUSES, status).toLowerCase()}!`);
      loadQuestions();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to update status"));
    }
  };

  const handleBulkStatus = async (ids, status) => {
    if (!ids.length || bulkBusy) return;
    setBulkBusy(true);
    try {
      const result = await bulkUpdateQuestionStatus(ids, status);
      if (result?.errors?.length) {
        toast.warning(
          `${result.message || "Some questions were not updated."} ${result.errors
            .map((e) => `#${e.id}: ${e.errorMessage}`)
            .join("; ")}`,
        );
      } else {
        toast.success(result?.message || "Status updated!");
      }
      setSelectedIds(new Set());
      loadQuestions();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to update status"));
    } finally {
      setBulkBusy(false);
    }
  };

  const handleArchive = async () => {
    const ids = archiveTarget?.ids || [];
    setArchiveTarget(null);
    if (ids.length === 1) {
      try {
        const result = await archiveQuestion(ids[0]);
        toast.success(result?.message || "Question archived!");
        loadQuestions();
      } catch (err) {
        toast.error(apiErrorMessage(err, "Failed to archive question"));
      }
    } else {
      await handleBulkStatus(ids, "ARCHIVED");
    }
  };

  const hierarchyForForm = { courses, subjects, chapters, topics, subTopics };
  const selectedList = Array.from(selectedIds);

  return (
    <Box sx={{ textAlign: "left" }}>
      <ToastContainer />

      <Card sx={{ boxShadow: 1, mb: 2.5 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", flex: "1 1 620px", alignItems: "center" }}>
              <TextField
                size="small"
                placeholder="Search questions..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: "#999" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 220 }}
              />
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Exam</InputLabel>
                <Select label="Exam" value={filterCascade.courseId} onChange={(e) => filterCascade.setCourseId(e.target.value)}>
                  <MenuItem value="">All Exams</MenuItem>
                  {sortByLabel(courses).map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Subject</InputLabel>
                <Select label="Subject" value={filterCascade.subjectId} onChange={(e) => filterCascade.setSubjectId(e.target.value)}>
                  <MenuItem value="">All Subjects</MenuItem>
                  {sortByLabel(filterCascade.subjectOptions).map((s) => (
                    <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Chapter</InputLabel>
                <Select label="Chapter" value={filterCascade.chapterId} onChange={(e) => filterCascade.setChapterId(e.target.value)}>
                  <MenuItem value="">All Chapters</MenuItem>
                  {sortByLabel(filterCascade.chapterOptions).map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Topic</InputLabel>
                <Select label="Topic" value={filterCascade.topicId} onChange={(e) => filterCascade.setTopicId(e.target.value)}>
                  <MenuItem value="">All Topics</MenuItem>
                  {sortByLabel(filterCascade.topicOptions).map((t) => (
                    <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Sub-Topic</InputLabel>
                <Select label="Sub-Topic" value={filterCascade.subTopicId} onChange={(e) => filterCascade.setSubTopicId(e.target.value)}>
                  <MenuItem value="">All Sub-Topics</MenuItem>
                  {sortByLabel(filterCascade.subTopicOptions).map((st) => (
                    <MenuItem key={st.id} value={st.id}>{st.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Difficulty</InputLabel>
                <Select label="Difficulty" value={difficultyFilter} onChange={(e) => setDifficultyFilter(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  {DIFFICULTIES.map((d) => (
                    <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Type</InputLabel>
                <Select label="Type" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                  <MenuItem value="">All Types</MenuItem>
                  {sortByLabel(QUESTION_TYPES).map((t) => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>Usage</InputLabel>
                <Select label="Usage" value={usageFilter} onChange={(e) => setUsageFilter(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  {sortByLabel(QUESTION_USAGES).map((u) => (
                    <MenuItem key={u.value} value={u.value}>{u.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Purpose</InputLabel>
                <Select label="Purpose" value={purposeFilter} onChange={(e) => setPurposeFilter(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  {sortByLabel(QUESTION_PURPOSES).map((p) => (
                    <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Final Output</InputLabel>
                <Select label="Final Output" value={finalOutputFilter} onChange={(e) => setFinalOutputFilter(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  {sortByLabel(FINAL_OUTPUTS).map((f) => (
                    <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>Status</InputLabel>
                <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  {sortByLabel(QUESTION_STATUSES).map((s) => (
                    <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="body2" color="text.secondary" sx={{ display: { xs: "none", md: "block" } }}>
                {totalElements} questions
              </Typography>
            </Box>

            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexShrink: 0 }}>
              <Button
                variant="outlined"
                startIcon={<ViewColumnIcon />}
                onClick={handleOpenColumnsMenu}
                sx={{ minWidth: { xs: "120px", md: "auto" }, fontSize: { xs: "10px", md: "12px" } }}
              >
                Columns
              </Button>
              <Menu
                anchorEl={columnsMenuAnchor}
                open={openColumnsMenu}
                onClose={handleCloseColumnsMenu}
                PaperProps={{ sx: { width: 200, maxHeight: 500, overflowY: "auto", borderRadius: 2 } }}
              >
                {allColumns.map((col) => (
                  <FormControlLabel
                    key={col.key}
                    control={<Checkbox checked={visibleColumns.has(col.key)} onChange={() => toggleColumn(col.key)} />}
                    label={col.label}
                    sx={{ pl: 1, pr: 2 }}
                  />
                ))}
              </Menu>
              <Button
                variant="contained"
                color="primary"
                startIcon={<UploadFileIcon />}
                onClick={() => setUploadOpen(true)}
                {...BUTTON_STYLES.primary}
                sx={{ minWidth: { xs: "120px", md: "auto" }, fontSize: { xs: "10px", md: "12px" } }}
              >
                Upload Content
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={openAdd}
                {...BUTTON_STYLES.primary}
                sx={{ minWidth: { xs: "120px", md: "auto" }, fontSize: { xs: "10px", md: "12px" } }}
              >
                Add Question
              </Button>
            </Stack>
          </Box>

          {selectedList.length > 0 && (
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              sx={{ mt: 2, pt: 2, borderTop: "1px solid", borderColor: "divider" }}
            >
              <Typography sx={{ fontWeight: 600, fontSize: "0.85rem" }}>{selectedList.length} selected</Typography>
              <Button
                size="small"
                variant="outlined"
                disabled={bulkBusy}
                startIcon={<PublishOutlinedIcon />}
                onClick={() => handleBulkStatus(selectedList, "PUBLISHED")}
              >
                Publish
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={bulkBusy}
                startIcon={<UndoOutlinedIcon />}
                onClick={() => handleBulkStatus(selectedList, "DRAFT")}
              >
                Move to Draft
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                disabled={bulkBusy}
                startIcon={<ArchiveOutlinedIcon />}
                onClick={() => setArchiveTarget({ ids: selectedList })}
              >
                Archive
              </Button>
              <Button size="small" disabled={bulkBusy} onClick={() => setSelectedIds(new Set())}>
                Clear selection
              </Button>
            </Stack>
          )}
        </CardContent>
      </Card>

      <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: 2, borderRadius: 3 }}>
        <TableContainer sx={{ maxHeight: "calc(100vh - 200px)", minHeight: 420 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell
                  padding="checkbox"
                  sx={{ backgroundColor: ACCENT, position: "sticky", top: 0, zIndex: 3 }}
                >
                  <Checkbox
                    size="small"
                    checked={allOnPageSelected}
                    indeterminate={someOnPageSelected && !allOnPageSelected}
                    onChange={toggleSelectAll}
                    sx={{ color: "#fff", "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: "#fff" } }}
                  />
                </TableCell>
                {visibleColumnDefs.map((c) => (
                  <TableCell
                    key={c.key}
                    align={c.cellCss?.align}
                    sx={{
                      ...c.cellCss,
                      padding: "10px 12px",
                      backgroundColor: ACCENT,
                      color: "white",
                      fontWeight: 600,
                      position: "sticky",
                      top: 0,
                      zIndex: 3,
                    }}
                    sortDirection={orderBy === c.key ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === c.key}
                      direction={orderBy === c.key ? order : "asc"}
                      onClick={() => handleSort(c.key)}
                      sx={{
                        color: "#ffffff !important",
                        "&.Mui-active": { color: "#ffffff !important" },
                        "& .MuiTableSortLabel-icon": { color: "#ffffff !important" },
                      }}
                    >
                      {c.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell
                  align="center"
                  sx={{
                    minWidth: "170px",
                    padding: "10px 12px",
                    backgroundColor: ACCENT,
                    color: "white",
                    fontWeight: 600,
                    position: "sticky",
                    top: 0,
                    right: 0,
                    zIndex: 4,
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <AssessmentTableSkeleton columns={visibleColumnDefs.length + 2} />
              ) : sortedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleColumnDefs.length + 2} sx={{ textAlign: "center", py: 4 }}>
                    <AssessmentEmptyState message="No questions found" hint="Try changing your search or filters." />
                  </TableCell>
                </TableRow>
              ) : (
                sortedData.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    selected={selectedIds.has(row.id)}
                    sx={{ "&:nth-of-type(odd)": { backgroundColor: "rgba(0,0,0,0.02)" } }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox size="small" checked={selectedIds.has(row.id)} onChange={() => toggleSelectRow(row.id)} />
                    </TableCell>
                    {visibleColumnDefs.map((c) => (
                      <TableCell key={c.key} align={c.cellCss?.align} sx={{ padding: "10px 12px", ...c.cellCss }}>
                        {(() => {
                          const value = c.getValue(row);
                          if (c.key === "question") {
                            return (
                              <Typography
                                component="div"
                                title={value}
                                sx={{
                                  fontWeight: 600,
                                  fontSize: "0.85rem",
                                  maxWidth: 420,
                                  whiteSpace: "normal",
                                  overflowWrap: "anywhere",
                                  "& p": { margin: 0 },
                                  "& p + p": { marginTop: "4px" },
                                  "& img": { maxHeight: 40, maxWidth: "100%" },
                                  ...mathTextSx,
                                }}
                                dangerouslySetInnerHTML={{ __html: sanitizeQuestionHtml(row.questionText) }}
                              />
                            );
                          }
                          if (c.key === "status") return <StatusChip value={value} />;
                          return value ?? "—";
                        })()}
                      </TableCell>
                    ))}
                    <TableCell
                      align="center"
                      sx={{ padding: "10px 12px", position: "sticky", right: 0, backgroundColor: "#fff", zIndex: 1 }}
                    >
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="Edit Question" arrow placement="top">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => openEdit(row)}
                            sx={{ "&:hover": { backgroundColor: "primary.light", color: "white" } }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {row.status !== "PUBLISHED" ? (
                          <Tooltip title="Publish Question" arrow placement="top">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => changeStatus(row.id, "PUBLISHED")}
                              sx={{ "&:hover": { backgroundColor: "success.light", color: "white" } }}
                            >
                              <PublishOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Move to Draft" arrow placement="top">
                            <IconButton
                              size="small"
                              onClick={() => changeStatus(row.id, "DRAFT")}
                              sx={{ "&:hover": { backgroundColor: "action.hover" } }}
                            >
                              <UndoOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {row.status !== "ARCHIVED" && (
                          <Tooltip title="Archive Question" arrow placement="top">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setArchiveTarget({ ids: [row.id] })}
                              sx={{ "&:hover": { backgroundColor: "error.light", color: "white" } }}
                            >
                              <ArchiveOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalElements}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: "1px solid",
            borderColor: "divider",
            "& .MuiTablePagination-toolbar": { paddingLeft: 2, paddingRight: 2 },
          }}
        />
      </Paper>

      <QuestionFormDialog
        open={formOpen}
        item={editing}
        hierarchy={hierarchyForForm}
        onClose={() => setFormOpen(false)}
        onSave={handleSave}
      />

      <BulkUploadDialog open={uploadOpen} onClose={() => setUploadOpen(false)} onUploaded={loadQuestions} />

      <DeleteConfirmationDialog
        open={Boolean(archiveTarget)}
        onClose={() => setArchiveTarget(null)}
        onConfirm={handleArchive}
        title="Confirm Archive"
        message={
          archiveTarget?.ids?.length > 1
            ? `Archive ${archiveTarget.ids.length} questions? They are kept but no longer available; you can publish or draft them again later.`
            : "Archive this question? It is kept but no longer available; you can publish or draft it again later."
        }
        confirmText="ARCHIVE"
        confirmButtonText="Archive"
        cancelButtonText="Cancel"
      />
    </Box>
  );
}

export default AssessmentQuestions;
